// Public SEO landing page for creations — indexable, shareable, with og:image.
import { createFileRoute, notFound, Link } from "@tanstack/react-router";
import { createServerFn } from "@tanstack/react-start";
import { createClient } from "@supabase/supabase-js";
import type { Database } from "@/integrations/supabase/types";
import { z } from "zod";

const getPublicCreation = createServerFn({ method: "GET" })
  .inputValidator((d: { slug: string }) => z.object({ slug: z.string().min(1).max(120) }).parse(d))
  .handler(async ({ data }) => {
    const key = process.env.SUPABASE_PUBLISHABLE_KEY!;
    const url = process.env.SUPABASE_URL!;
    const client = createClient<Database>(url, key, {
      auth: { persistSession: false, autoRefreshToken: false },
      global: {
        fetch: (input, init) => {
          const h = new Headers(init?.headers);
          if (key.startsWith("sb_") && h.get("Authorization") === `Bearer ${key}`) h.delete("Authorization");
          h.set("apikey", key);
          return fetch(input, { ...init, headers: h });
        },
      },
    });
    const { data: row, error } = await client
      .from("creations")
      .select("slug, title, ai_summary, ai_category, seo_title, seo_description, artwork_url, published_at, tags, body")
      .eq("slug", data.slug)
      .eq("status", "published")
      .is("archived_at", null)
      .maybeSingle();
    if (error) throw new Error(error.message);
    if (!row) return null;
    return row;
  });

export const Route = createFileRoute("/release/$slug")({
  loader: async ({ params }) => {
    const row = await getPublicCreation({ data: { slug: params.slug } });
    if (!row) throw notFound();
    return row;
  },
  head: ({ loaderData }) => {
    if (!loaderData) return { meta: [{ title: "Not found · KAfrequency" }] };
    const title = loaderData.seo_title || `${loaderData.title} · KAfrequency`;
    const desc = loaderData.seo_description || loaderData.ai_summary || loaderData.title;
    const image = loaderData.artwork_url;
    const canonical = `https://buddhaka.com/release/${loaderData.slug}`;
    return {
      meta: [
        { title },
        { name: "description", content: desc },
        { property: "og:title", content: title },
        { property: "og:description", content: desc },
        { property: "og:type", content: "article" },
        { property: "og:url", content: canonical },
        ...(image ? [
          { property: "og:image", content: image },
          { name: "twitter:image", content: image },
        ] : []),
        { name: "twitter:card", content: image ? "summary_large_image" : "summary" },
        { name: "twitter:title", content: title },
        { name: "twitter:description", content: desc },
      ],
      links: [{ rel: "canonical", href: canonical }],
    };
  },
  notFoundComponent: () => (
    <div className="mx-auto max-w-2xl px-4 py-24 text-center">
      <h1 className="font-serif text-3xl text-gold-luminous">Release not found</h1>
      <p className="mt-3 text-foreground/70">This creation has been archived or the link is incorrect.</p>
      <Link to="/" className="mt-6 inline-block text-gold underline underline-offset-4">Return home →</Link>
    </div>
  ),
  errorComponent: ({ error, reset }) => (
    <div className="mx-auto max-w-2xl px-4 py-24 text-center">
      <h1 className="font-serif text-2xl text-gold-luminous">Something wobbled</h1>
      <p className="mt-3 text-sm text-foreground/60">{(error as Error).message}</p>
      <button onClick={reset} className="mt-4 rounded border border-gold/40 px-4 py-2 text-sm text-gold">Retry</button>
    </div>
  ),
  component: ReleasePage,
});

function ReleasePage() {
  const c = Route.useLoaderData();
  return (
    <article className="mx-auto max-w-3xl px-4 py-12 md:py-20">
      {c.artwork_url && (
        <img
          src={c.artwork_url}
          alt={c.title}
          className="mb-8 aspect-square w-full rounded-lg border border-gold/20 object-cover shadow-2xl"
          loading="eager"
        />
      )}
      {c.ai_category && (
        <div className="mb-3 text-xs uppercase tracking-[0.24em] text-gold/70">{c.ai_category}</div>
      )}
      <h1 className="font-serif text-4xl leading-tight text-gold-luminous md:text-5xl">{c.title}</h1>
      {c.ai_summary && <p className="mt-4 text-lg text-foreground/80">{c.ai_summary}</p>}
      {c.body && (
        <div className="prose prose-invert mt-8 max-w-none text-foreground/90">
          {c.body.split("\n\n").map((p: string, i: number) => (
            <p key={i} className="my-4 leading-relaxed">{p}</p>
          ))}
        </div>
      )}
      {c.tags && c.tags.length > 0 && (
        <div className="mt-8 flex flex-wrap gap-2">
          {c.tags.map((t: string) => (
            <span key={t} className="rounded-full border border-gold/30 px-3 py-1 text-xs text-foreground/70">#{t}</span>
          ))}
        </div>
      )}
      {c.published_at && (
        <div className="mt-8 text-xs text-foreground/40">Published {new Date(c.published_at).toLocaleDateString()}</div>
      )}
      <div className="mt-12 rounded-lg border border-gold/20 bg-black/40 p-6">
        <div className="text-xs uppercase tracking-widest text-gold/70">Continue</div>
        <div className="mt-3 flex flex-wrap gap-3 text-sm">
          <Link to="/" className="text-gold underline underline-offset-4">Enter the temple →</Link>
          <Link to="/activations" className="text-gold underline underline-offset-4">Today's activation →</Link>
          <Link to="/music" className="text-gold underline underline-offset-4">Music →</Link>
        </div>
      </div>
    </article>
  );
}
